import React, { useContext } from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';
import Alert from '@digital-hig/mui/Alert';
import AlertIcon from '@digital-hig/mui/dist/AlertIcon';
import LoadingOverlay from 'react-loading-overlay';

import { useCollectionsList } from '../../../apis/collections/hooks';
import { AUDefaultContent } from '../../../components/presentational/AUDefaultContent';
import CollectionsTable from '../../../components/presentational/CollectionsTable/CollectionsTable';
import languageOptions from '../../../helpers/languageOptions';
import I18nContext from '../../contexts/I18nContext';
import LoginContext from '../../contexts/LoginContext';
import { generatePlaylistPathname } from '../playlist/utils';

interface Props {
  langCode: string;
  baseURL?: string;
}

function PlaylistListing({ langCode, baseURL }: Props) {
  const { t, i18n } = useContext(I18nContext);

  const locale = langCode || i18n.language;
  const { userProfile, isUserLoading, isLoggedIn } = useContext(LoginContext);
  const { data, isLoading } = useCollectionsList({
    baseURL,
    provider: 'aws',
    enabled: Boolean(userProfile?.sub),
  });

  const playlistsOnly = data || [];
  return (
    <div className="au-w-full au-grow">
      <LoadingOverlay
        active={isUserLoading || isLoading}
        spinner
        fadeSpeed={250}
        styles={{
          overlay: (base: object) => ({
            ...base,
            position: 'fixed',
            zIndex: 94035,
          }),
        }}
      />
      {!isUserLoading && !isLoggedIn && (
        <Alert
          icon={<AlertIcon severity="error" variant="outlined" />}
          severity="error"
          variant="outlined"
          className="!au-text-white"
        >
          {t('error_401', { ns: 'common' })}
        </Alert>
      )}
      {!isLoading && isLoggedIn && playlistsOnly?.length === 0 && (
        <AUDefaultContent type="playlists" />
      )}
      {!isLoading && playlistsOnly?.length > 0 && (
        <CollectionsTable
          langCode={locale}
          entries={
            playlistsOnly?.map(entry => ({
              title: entry.title,
              description: t('{{count}} items', {
                ns: 'common',
                count: entry.collection_items.length,
              }),
              url: generatePlaylistPathname(entry.id, locale),
              editLink:
                userProfile?.sub === entry.creator_id
                  ? generatePlaylistPathname(entry.id, locale)
                  : undefined,
              timestamp: entry.date_created,
              isPublic: entry.is_public ?? false,
            })) || []
          }
        />
      )}
    </div>
  );
}

// Shared attribute definition for Block Editor and AURR.
PlaylistListing.attributes = {
  baseURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  userProfileURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  userContentURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  langCode: {
    type: 'string',
    enum: localeConfig.urlLocales,
    default: localeConfig.defaultURLLocale,
    list: languageOptions,
  },
};

export default PlaylistListing;
